import React from 'react';
import { Phone, Wrench, AlertTriangle, CheckCircle, Search } from 'lucide-react';
export function FuelInjection() {
  return <div className="min-h-screen bg-gray-50" data-id="element-1154">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-1155">
        <div className="container mx-auto px-4" data-id="element-1156">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-1157">
            Automotive Fuel Injection
          </h1>
          <p className="text-xl text-gray-100 text-center italic" data-id="element-1158">
            Serving Farmington Hills, Farmington, Novi, Livonia, Northville
            Since 1958
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-1159">
        <div className="max-w-4xl mx-auto" data-id="element-1160">
          {/* Introduction */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1161">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-1162">
              Clean fuel injectors are a must for peak engine performance and
              fuel economy.
            </h2>

            <p className="text-xl text-gray-700 text-center italic mb-8" data-id="element-1163">
              If the injectors are dirty and can't deliver their normal dose of
              fuel, performance, <strong data-id="element-1164">fuel mileage</strong> becomes very
              poor.
            </p>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-1165">
              <img src="//drwzpk38qkpfb.cloudfront.net/www.garrettauto.com/uploaded/images/auto%20truck%20suppliers/New%20Folder/epFuelInjectors.png?opt=w3" alt="Fuel injectors" className="w-full h-[280px] object-contain rounded-lg" data-id="element-1166" />
              <img src="//drwzpk38qkpfb.cloudfront.net/core/getthumb?file=uploaded%2Fimages%2Fauto%20truck%20suppliers%2FNew%20Folder%2Fvalve.jpg&site=1986&w=275&h=319&user=1617&opt=w3" alt="Engine valve" className="w-full h-[280px] object-contain rounded-lg" data-id="element-1167" />
            </div>
          </div>

          {/* Dirty Valves Section */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1168">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center italic" data-id="element-1169">
              Dirty valves can cause
            </h2>

            <div className="grid md:grid-cols-2 gap-8 mb-6" data-id="element-1170">
              <div className="flex justify-center" data-id="element-1171">
                <img src="//drwzpk38qkpfb.cloudfront.net/www.garrettauto.com/uploaded/images/auto%20truck%20suppliers/New%20Folder/texaco_intake_valve.jpg?opt=w3" alt="Clean valve vs dirty valve comparison" className="max-w-[300px] h-auto rounded-lg" data-id="element-1172" />
              </div>

              <div className="space-y-3" data-id="element-1173">
                <div className="flex items-start" data-id="element-1174">
                  <AlertTriangle className="text-red-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-1175" />
                  <span className="text-gray-700" data-id="element-1176">Lost fuel economy</span>
                </div>
                <div className="flex items-start" data-id="element-1177">
                  <AlertTriangle className="text-red-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-1178" />
                  <span className="text-gray-700" data-id="element-1179">
                    Lost power and poor throttle response
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1180">
                  <AlertTriangle className="text-red-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-1181" />
                  <span className="text-gray-700" data-id="element-1182">Failed emission tests</span>
                </div>
                <div className="flex items-start" data-id="element-1183">
                  <AlertTriangle className="text-red-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-1184" />
                  <span className="text-gray-700" data-id="element-1185">
                    Poor drivability - surging, hesitation, stalling, rough idle
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1186">
                  <AlertTriangle className="text-red-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-1187" />
                  <span className="text-gray-700" data-id="element-1188">
                    Engine knocking (pinging) and rap
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1189">
                  <AlertTriangle className="text-red-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-1190" />
                  <span className="text-gray-700" data-id="element-1191">Difficult starts</span>
                </div>
              </div>
            </div>

            <p className="text-center text-gray-600 font-semibold mt-4" data-id="element-1192">
              Clean Valve vs Dirty Valve
            </p>
          </div>

          {/* BG Fuel Injection Cleaning Results */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1193">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-1194">
              BG Fuel Injection Cleaning Service Results
            </h2>

            <p className="text-gray-700 text-center italic mb-6" data-id="element-1195">
              Two of the four dirty injectors tested were fouled &gt;15% and
              another was fouled &gt;10%. After BG fuel injection cleaning
              service the injectors returned to &gt;95% flow.
            </p>

            <div className="flex justify-center mb-8" data-id="element-1196">
              <img src="http://www.goodoil4u.com/images/api.ht2.jpg" alt="Before and After fuel injection cleaning" className="max-w-full h-auto rounded-lg border shadow-md" data-id="element-1197" />
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-6" data-id="element-1198">
              <p className="text-gray-800 text-center" data-id="element-1199">
                BG induction service averaged{' '}
                <strong data-id="element-1200">72% intake valve deposit cleanup</strong> across a wide
                range of engine types and sizes, with two cars cleaning up
                greater than 90%.
              </p>
            </div>

            <div className="flex justify-center" data-id="element-1201">
              <img src="http://www.goodoil4u.com/images/api.ht3.jpg" alt="Intake valve deposit cleanup results" className="max-w-full h-auto rounded-lg border shadow-md" data-id="element-1202" />
            </div>
          </div>

          {/* Benefits Section */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1203">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1204">
              Benefits of Professional Fuel Injection Cleaning
            </h2>

            <div className="space-y-4" data-id="element-1205">
              <div className="flex items-start" data-id="element-1206">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-1207" />
                <div data-id="element-1208">
                  <h3 className="font-bold text-gray-900 mb-1" data-id="element-1209">
                    Restore Fuel Economy
                  </h3>
                  <p className="text-gray-700" data-id="element-1210">
                    Clean injectors deliver the proper fuel mixture for optimal
                    efficiency
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-1211">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-1212" />
                <div data-id="element-1213">
                  <h3 className="font-bold text-gray-900 mb-1" data-id="element-1214">
                    Improve Performance
                  </h3>
                  <p className="text-gray-700" data-id="element-1215">
                    Restore lost power and throttle response
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-1216">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-1217" />
                <div data-id="element-1218">
                  <h3 className="font-bold text-gray-900 mb-1" data-id="element-1219">
                    Pass Emissions Tests
                  </h3>
                  <p className="text-gray-700" data-id="element-1220">
                    Clean fuel system helps your vehicle meet emission standards
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-1221">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-1222" />
                <div data-id="element-1223">
                  <h3 className="font-bold text-gray-900 mb-1" data-id="element-1224">
                    Smoother Operation
                  </h3>
                  <p className="text-gray-700" data-id="element-1225">
                    Eliminate rough idle, hesitation, and stalling
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-1226">
            <h2 className="text-3xl font-bold mb-4" data-id="element-1227">
              Need Fuel Injection Cleaning?
            </h2>
            <p className="text-xl mb-6 italic" data-id="element-1228">If we can help give us a call</p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-1229">
              <Phone className="mr-3" size={24} data-id="element-1230" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-1231">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}